

import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  opsApi, apiError, fmtINR, fmtINR2, fmtDate, fmtNum, GRAD, FONT_HEAD,
  STAGES, STAGE_META, StatusPill, INVOICE_STATUS, APPROVAL_STATUS, KIT_STATUS,
  HEALTH_STATUS, TICKET_STATUS, ROLE_RANK,
} from './opsApi';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  ArrowLeft, Rocket, Link2, KeyRound, GraduationCap, Users, BadgePercent,
  Receipt, LifeBuoy, MapPin, Phone, Mail, Copy, Ban, CheckCircle2, Cpu, Gauge,
} from 'lucide-react';

const STATUS_STYLES_ST = {
  open: 'bg-blue-50 text-blue-600 border-blue-100',
  in_progress: 'bg-amber-50 text-amber-600 border-amber-100',
  resolved: 'bg-emerald-50 text-emerald-600 border-emerald-100',
  closed: 'bg-gray-50 text-gray-600 border-gray-100',
};
const PRIORITY_STYLES_ST = {
  low: 'bg-gray-50 text-gray-500 border-gray-100',
  medium: 'bg-amber-50 text-amber-500 border-amber-100',
  high: 'bg-orange-50 text-orange-500 border-orange-100',
  urgent: 'bg-red-50 text-red-500 border-red-100',
};
const ST_TYPE_LABELS = {
  software_issue: 'Software Issue', billing_question: 'Billing Question',
  account_issue: 'Account Issue', feature_request: 'Feature Request', other: 'Other',
};

function InstitutionSupportTickets({ instId, inst, navigate }) {
  const [tickets, setTickets] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    if (!inst?.tenant_id) { setLoading(false); return; }
    opsApi.get('/support-tickets', { params: { tenant_id: inst.tenant_id, page_size: 50 } })
      .then((r) => setTickets(r.data.items || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [inst]);

  if (!inst?.tenant_id) {
    return <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center text-sm text-slate-400">Activate the institution to view support tickets.</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-700" style={FONT_HEAD}>Support Tickets</h3>
        <Button onClick={() => navigate('/sales/support-tickets')} className="h-8 px-3 text-xs rounded-lg text-white border-0" style={{ background: GRAD }}>
          View All Tickets
        </Button>
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-10"><div className="animate-spin rounded-full h-6 w-6 border-b-2" style={{ borderColor: GRAD }} /></div>
        ) : tickets.length === 0 ? (
          <p className="p-6 text-sm text-slate-400">No support tickets for this institution.</p>
        ) : (
          tickets.map((t) => (
            <div key={t.id} className="px-6 py-4 border-b border-slate-50 hover:bg-slate-50/50 cursor-pointer transition-colors" onClick={() => navigate(`/sales/support-tickets/${t.id}`)}>
              <div className="flex items-center justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className={`text-[10px] px-2.5 py-0.5 rounded-full font-bold border ${STATUS_STYLES_ST[t.status] || STATUS_STYLES_ST.open}`}>{(t.status || 'open').replace('_', ' ').toUpperCase()}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold border ${PRIORITY_STYLES_ST[t.priority] || PRIORITY_STYLES_ST.medium}`}>{(t.priority || 'medium').toUpperCase()}</span>
                  </div>
                  <div className="text-sm font-medium text-slate-800 truncate">{t.title}</div>
                  <div className="text-xs text-slate-400">{ST_TYPE_LABELS[t.type] || t.type} · {fmtDate(t.created_at)}</div>
                </div>
                <span className="text-[10px] text-slate-300 font-mono">#{t.id?.slice(0, 8).toUpperCase()}</span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

const Stat = ({ icon: Icon, label, value }) => (
  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
    <div className="h-0.5" style={{ background: GRAD }} />
    <div className="p-4">
      <div className="flex items-center gap-2 text-xs text-slate-400 mb-1.5"><Icon className="w-3.5 h-3.5 text-[#34A853]" />{label}</div>
      <div className="text-xl font-semibold tabular-nums" style={FONT_HEAD}>{value}</div>
    </div>
  </div>
);

export default function OpsInstitutionDetail({ user }) {
  const { instId } = useParams();
  const navigate = useNavigate();
  const [inst, setInst] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [activity, setActivity] = useState([]);
  const [dialog, setDialog] = useState(null); // activate | link | discount | refund | ticket
  const [activationResult, setActivationResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [plans, setPlans] = useState([]);
  const rank = ROLE_RANK[user.role] || 0;

  const [actForm, setActForm] = useState({ admin_email: '', admin_password: '', admin_name: '', plan_id: '' });
  const [linkCode, setLinkCode] = useState('');
  const [discForm, setDiscForm] = useState({ discount_pct: '', reason: '' });
  const [refundForm, setRefundForm] = useState({ invoice_id: '', amount: '', reason: '' });
  const [ticketForm, setTicketForm] = useState({ title: '', description: '', priority: 'medium' });

  const load = useCallback(() => {
    opsApi.get(`/institutions/${instId}`).then((r) => setInst(r.data)).catch(() => navigate('/sales/institutions'));
    opsApi.get('/invoices', { params: { institution_id: instId, page_size: 50 } }).then((r) => setInvoices(r.data.items)).catch(() => {});
    opsApi.get('/approvals', { params: { page_size: 50 } }).then((r) => setApprovals(r.data.items.filter((a) => a.institution_id === instId))).catch(() => {});
    opsApi.get('/tickets', { params: { page_size: 50 } }).then((r) => setTickets(r.data.items.filter((t) => t.institution_id === instId))).catch(() => {});
    opsApi.get(`/institutions/${instId}/activity`).then((r) => setActivity(r.data)).catch(() => {});
  }, [instId, navigate]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    opsApi.get('/plans')
      .then((r) => setPlans(r.data.filter((p) => p.active)))
      .catch((e) => { /* show once on initial load */ });
  }, []);

  useEffect(() => {
    if (dialog === 'activate') {
      opsApi.get('/plans')
        .then((r) => setPlans(r.data.filter((p) => p.active)))
        .catch((e) => toast.error(apiError(e)));
    }
  }, [dialog]);

  if (!inst) {
    return <div className="flex justify-center py-24"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#34A853]" /></div>;
  }

  const activated = !!inst.tenant_id;
  const bill = inst.bill_preview;

  const act = async (fn, success) => {
    setBusy(true);
    try { await fn(); if (success) toast.success(success); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const moveStage = (stage) => act(() => opsApi.post(`/institutions/${instId}/stage`, { stage }), `Moved to ${STAGE_META[stage].label}`);

  const activate = async () => {
    if (!actForm.admin_email || !actForm.admin_password) { toast.error('Admin email and password are required'); return; }
    if (!actForm.plan_id) { toast.error('Select a plan to activate with'); return; }
    setBusy(true);
    try {
      const { data } = await opsApi.post(`/institutions/${instId}/activate`, actForm);
      setActivationResult(data);
      toast.success('Institution activated on the Desk platform!');
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const copy = (text) => { navigator.clipboard.writeText(text); toast.success('Copied'); };
  const closeDialog = () => setDialog(null);
  const paidInvoices = invoices.filter((i) => i.status === 'paid');

  return (
    <div className="space-y-6" data-testid="institution-detail-page">
      <div>
        <Link to="/sales/institutions" className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-800 mb-3">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to institutions
        </Link>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl md:text-3xl font-semibold tracking-tight" style={FONT_HEAD}>{inst.name}</h1>
              <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${STAGE_META[inst.stage]?.color}`}>{STAGE_META[inst.stage]?.label}</span>
              <span className="px-2 py-0.5 rounded-md text-[10px] font-mono text-[#3B82F6] bg-blue-50 border border-blue-100">{inst.scope_path}</span>
              {inst.tenant?.status === 'suspended' && <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-red-50 text-red-600 border border-red-200">Tenant suspended</span>}
            </div>
            <div className="flex items-center gap-4 text-xs text-slate-500 mt-2 flex-wrap">
              {inst.city && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{inst.city}</span>}
              {inst.contact_email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{inst.contact_email}</span>}
              {inst.contact_phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{inst.contact_phone}</span>}
              <span>Rep: <b className="text-slate-700">{inst.assigned_rep_name || '—'}</b></span>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            {!activated && (
              <>
                <Button data-testid="activate-institution-btn" onClick={() => { setActivationResult(null); setDialog('activate'); }}
                  className="text-white rounded-xl h-9 border-0" style={{ background: GRAD }}>
                  <Rocket className="w-4 h-4 mr-1.5" /> Activate institution
                </Button>
                <Button data-testid="link-tenant-btn" variant="outline" onClick={() => setDialog('link')} className="rounded-xl h-9">
                  <Link2 className="w-4 h-4 mr-1.5" /> Link existing tenant
                </Button>
              </>
            )}
            {activated && rank >= 3 && (
              inst.tenant?.status === 'suspended'
                ? <Button onClick={() => act(() => opsApi.post(`/institutions/${instId}/tenant-status`, { status: 'active' }), 'Tenant reactivated')} variant="outline" className="rounded-xl h-9 text-green-700 border-green-200"><CheckCircle2 className="w-4 h-4 mr-1.5" />Reactivate tenant</Button>
                : <Button data-testid="suspend-tenant-btn" onClick={() => act(() => opsApi.post(`/institutions/${instId}/tenant-status`, { status: 'suspended' }), 'Tenant suspended')} variant="outline" className="rounded-xl h-9 text-red-600 border-red-200"><Ban className="w-4 h-4 mr-1.5" />Suspend tenant</Button>
            )}
          </div>
        </div>
      </div>

      {!activated && inst.stage !== 'churned' && (
        <div className="bg-white rounded-2xl border border-slate-200 p-4 flex items-center gap-2 flex-wrap">
          <span className="text-xs text-slate-400 mr-2">Lifecycle:</span>
          {STAGES.filter((s) => s !== 'active').map((s) => (
            <button key={s} data-testid={`move-stage-${s}`} onClick={() => s !== inst.stage && moveStage(s)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                s === inst.stage ? 'text-white border-transparent' : 'bg-white text-slate-600 border-slate-200 hover:border-[#34A853]'
              }`}
              style={s === inst.stage ? { background: GRAD } : undefined}>
              {STAGE_META[s].label}
            </button>
          ))}
        </div>
      )}

      {activated && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <Stat icon={GraduationCap} label="Teacher licences" value={fmtNum(inst.usage?.teachers_active ?? 0)} />
          <Stat icon={Users} label="Licensed students" value={fmtNum(inst.usage?.students_licensed ?? 0)} />
          <Stat icon={Gauge} label="Penetration" value={inst.penetration_pct != null ? `${inst.penetration_pct}%` : '—'} />
          <Stat icon={Cpu} label="Kits deployed" value={fmtNum(inst.kit_count ?? 0)} />
          <Stat icon={Receipt} label="Next bill (est.)" value={bill ? fmtINR(bill.total) : '—'} />
        </div>
      )}

      <Tabs defaultValue="overview">
        <TabsList className="bg-white border border-slate-200 rounded-xl p-1 h-auto flex-wrap">
          {['overview', 'billing', 'approvals', 'kits', 'resolutions', 'support tickets', 'activity'].map((t) => (
            <TabsTrigger key={t} value={t} data-testid={`inst-tab-${t.replace(' ', '-')}`}
              className="rounded-lg px-4 py-2 text-sm capitalize data-[state=active]:bg-[#34A853] data-[state=active]:text-white">
              {t}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* OVERVIEW */}
        <TabsContent value="overview" className="mt-5 grid lg:grid-cols-2 gap-5">
          <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-3">
            <h3 className="text-sm font-semibold" style={FONT_HEAD}>Account details</h3>
            {[
              ['Type', inst.institution_type],
              ['Contact person', inst.contact_name],
              ['Billing cycle', inst.billing_cycle],
              ['Expected size', `${inst.expected_teachers} teachers · ${inst.expected_students} students`],
              ['Approved discount', `${inst.discount_pct || 0}%`],
              ['Created', fmtDate(inst.created_at)],
              ['Activated', inst.activated_at ? fmtDate(inst.activated_at) : 'Not yet'],
              ['Notes', inst.notes],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between gap-4 text-sm border-b border-slate-50 pb-2">
                <span className="text-slate-400">{k}</span><span className="text-slate-700 text-right capitalize">{v || '—'}</span>
              </div>
            ))}
          </div>
          {activated && inst.tenant && (
            <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-3">
              <h3 className="text-sm font-semibold flex items-center gap-2" style={FONT_HEAD}>
                <KeyRound className="w-4 h-4 text-[#34A853]" /> Platform tenant (school dashboard)
              </h3>
              {[
                ['Institution code', inst.tenant.institution_code],
                ['Licence key', inst.tenant.licence_key],
                ['Tenant status', inst.tenant.status],
                ['Login URL', `/${inst.tenant.slug}`],
                ['Licence expiry', fmtDate(inst.tenant.licence_expiry)],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between items-center gap-4 text-sm border-b border-slate-50 pb-2">
                  <span className="text-slate-400">{k}</span>
                  <span className="flex items-center gap-1.5 text-slate-700 font-mono text-xs">
                    {v || '—'}
                    {v && ['Institution code', 'Licence key', 'Login URL'].includes(k) && (
                      <button onClick={() => copy(v)} className="text-slate-300 hover:text-[#34A853]"><Copy className="w-3 h-3" /></button>
                    )}
                  </span>
                </div>
              ))}
              <p className="text-[11px] text-slate-400 pt-1">Licenses are billed to this institution only — student accounts are free. Super Credits are a separate global student wallet, never on institution bills.</p>
            </div>
          )}
        </TabsContent>

        {/* BILLING */}
        <TabsContent value="billing" className="mt-5 space-y-5">
          {!activated ? (
            <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center text-sm text-slate-400">Activate the institution to start billing.</div>
          ) : (
            <>
              {bill && (
                <div className="bg-white rounded-2xl border border-slate-200 p-6">
                  <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                    <h3 className="text-sm font-semibold" style={FONT_HEAD}>
                      Live bill preview <span className="ml-2 text-xs font-normal text-slate-400">Tier: <b className="text-[#34A853]">{bill.tier?.name}</b> · {bill.billing_cycle}</span>
                    </h3>
                    <Button data-testid="generate-invoice-btn" disabled={busy}
                      onClick={() => act(async () => {
                        const { data } = await opsApi.post(`/institutions/${instId}/invoices`);
                        toast.success(`Invoice ${data.invoice_number} generated — ${fmtINR2(data.total)}`);
                      })}
                      className="text-white rounded-xl h-9 border-0" style={{ background: GRAD }}>
                      <Receipt className="w-4 h-4 mr-1.5" /> Generate invoice
                    </Button>
                  </div>
                  <div className="text-sm space-y-2" data-testid="bill-preview">
                    <div className="flex justify-between"><span className="text-slate-500">Teacher Licences ({bill.teachers_billed} teachers × {bill.avg_teacher_months} months × ₹{bill.per_teacher_price}/mo)</span><span className="tabular-nums">{fmtINR2(bill.desky_teacher_cost)}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Student Licences ({bill.students_billed} students × {bill.avg_student_months} months × ₹{bill.per_student_price}/mo)</span><span className="tabular-nums">{fmtINR2(bill.desky_student_cost)}</span></div>
                    {bill.hub_storage?.base_cost > 0 && (
                      <div className="flex justify-between">
                        <span className="text-slate-500">DeskeyHUB Storage — {bill.hub_storage.plan_name || 'Storage Plan'} ({bill.hub_storage.months_billed} months × ₹{bill.hub_storage.monthly_price}/mo)</span>
                        <span className="tabular-nums">{fmtINR2(bill.hub_storage.base_cost)}</span>
                      </div>
                    )}
                    <div className="flex justify-between border-t border-slate-100 pt-2"><span className="text-slate-500">Subtotal</span><span className="tabular-nums">{fmtINR2(bill.subtotal)}</span></div>
                    {bill.discount_pct > 0 && <div className="flex justify-between text-amber-600"><span>Discount ({bill.discount_pct}%)</span><span className="tabular-nums">−{fmtINR2(bill.discount_amount)}</span></div>}
                    <div className="flex justify-between"><span className="text-slate-500">CGST (9%)</span><span className="tabular-nums">{fmtINR2(bill.cgst)}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">SGST (9%)</span><span className="tabular-nums">{fmtINR2(bill.sgst)}</span></div>
                    <div className="flex justify-between border-t border-slate-200 pt-2 text-base font-semibold"><span>Total</span><span className="tabular-nums text-[#34A853]" data-testid="bill-total">{fmtINR2(bill.total)}</span></div>
                  </div>
                </div>
              )}
              <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-100 text-sm font-semibold" style={FONT_HEAD}>Invoices</div>
                {invoices.length === 0 ? <p className="p-6 text-sm text-slate-400">No invoices yet.</p> : (
                  <table className="w-full text-sm">
                    <tbody>
                      {invoices.map((inv) => (
                        <tr key={inv.id} className="border-b border-slate-50">
                          <td className="px-6 py-3 font-mono text-xs">{inv.invoice_number}</td>
                          <td className="px-4 py-3 text-xs text-slate-500">{fmtDate(inv.created_at)}</td>
                          <td className="px-4 py-3 tabular-nums font-medium">{fmtINR2(inv.total)}</td>
                          <td className="px-4 py-3"><StatusPill value={inv.status} map={INVOICE_STATUS} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}
        </TabsContent>

        {/* APPROVALS */}
        <TabsContent value="approvals" className="mt-5 space-y-4">
          <div className="flex justify-between items-center flex-wrap gap-2">
            <p className="text-sm text-slate-500">Discounts &amp; refunds escalate UP the hierarchy for approval.</p>
            <div className="flex gap-2">
              <Button data-testid="request-discount-btn" onClick={() => setDialog('discount')} variant="outline" className="rounded-xl h-9">
                <BadgePercent className="w-4 h-4 mr-1.5" /> Request discount
              </Button>
              <Button data-testid="request-refund-btn" onClick={() => setDialog('refund')} variant="outline" className="rounded-xl h-9"
                disabled={paidInvoices.length === 0} title={paidInvoices.length === 0 ? 'No paid invoices to refund' : ''}>
                <Receipt className="w-4 h-4 mr-1.5" /> Request refund
              </Button>
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            {approvals.length === 0 ? <p className="p-6 text-sm text-slate-400">No approval requests for this institution.</p> : (
              approvals.map((a) => (
                <div key={a.id} className="px-6 py-4 border-b border-slate-50 flex items-center justify-between gap-4 flex-wrap">
                  <div>
                    <div className="text-sm font-medium capitalize">{a.kind === 'discount' ? `${a.discount_pct}% discount` : `${fmtINR2(a.amount)} refund (${a.invoice_number})`}</div>
                    <div className="text-xs text-slate-400">{a.reason} · by {a.requested_by_name} · {fmtDate(a.created_at)}</div>
                    {a.decision_note && <div className="text-xs text-slate-400 italic">Note: {a.decision_note}</div>}
                  </div>
                  <StatusPill value={a.status} map={APPROVAL_STATUS} />
                </div>
              ))
            )}
          </div>
        </TabsContent>

        {/* KITS */}
        <TabsContent value="kits" className="mt-5">
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <span className="text-sm font-semibold" style={FONT_HEAD}>Hardware kits at this institution</span>
              <Link to="/sales/fleet" className="text-xs text-[#3B82F6] hover:underline">Manage fleet →</Link>
            </div>
            {(!inst.kits || inst.kits.length === 0) ? <p className="p-6 text-sm text-slate-400">No kits allocated. Allocate from the Hardware Fleet module.</p> : (
              <table className="w-full text-sm">
                <tbody>
                  {inst.kits.map((k) => (
                    <tr key={k.id} className="border-b border-slate-50">
                      <td className="px-6 py-3 font-mono text-xs">{k.serial_number}</td>
                      <td className="px-4 py-3 text-xs text-slate-500">{k.model}</td>
                      <td className="px-4 py-3"><StatusPill value={k.status} map={KIT_STATUS} /></td>
                      <td className="px-4 py-3"><StatusPill value={k.health} map={HEALTH_STATUS} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </TabsContent>

        {/* RESOLUTIONS */}
        <TabsContent value="resolutions" className="mt-5 space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-slate-500">Issues assigned to this institution's rep for resolution.</p>
            <Button data-testid="open-ticket-btn" onClick={() => setDialog('ticket')} variant="outline" className="rounded-xl h-9">
              <LifeBuoy className="w-4 h-4 mr-1.5" /> Open ticket
            </Button>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            {tickets.length === 0 ? <p className="p-6 text-sm text-slate-400">No tickets for this institution.</p> : (
              tickets.map((t) => (
                <div key={t.id} className="px-6 py-4 border-b border-slate-50 flex items-center justify-between gap-4">
                  <div>
                    <div className="text-sm font-medium">{t.title}</div>
                    <div className="text-xs text-slate-400">{t.priority} priority · {fmtDate(t.created_at)}</div>
                    {t.resolution_note && <div className="text-xs text-green-600">Resolution: {t.resolution_note}</div>}
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusPill value={t.status} map={TICKET_STATUS} />
                    {t.status !== 'resolved' && (
                      <Button size="sm" disabled={busy} data-testid={`resolve-ticket-${t.id}`}
                        onClick={() => {
                          const note = window.prompt('Resolution note:');
                          if (note != null) act(() => opsApi.put(`/tickets/${t.id}`, { status: 'resolved', resolution_note: note }), 'Ticket resolved');
                        }}
                        className="h-7 text-xs rounded-lg bg-[#34A853] hover:bg-[#2D9249] text-white">Resolve</Button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </TabsContent>

        {/* SUPPORT TICKETS */}
        <TabsContent value="support tickets" className="mt-5">
          <InstitutionSupportTickets instId={instId} inst={inst} navigate={navigate} />
        </TabsContent>

        {/* ACTIVITY */}
        <TabsContent value="activity" className="mt-5">
          <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4">
            {activity.length === 0 ? <p className="text-sm text-slate-400">No activity recorded.</p> : activity.map((a) => (
              <div key={a.id} className="flex gap-3 text-sm border-b border-slate-50 pb-3">
                <div className="w-1.5 h-1.5 rounded-full bg-[#3B82F6] mt-2 shrink-0" />
                <div>
                  <span className="font-medium">{a.actor_name}</span> <span className="text-slate-400">{a.action.replace(/[._]/g, ' ')}</span>
                  {a.detail && <div className="text-xs text-slate-500">{a.detail}</div>}
                  <div className="text-[10px] text-slate-300">{fmtDate(a.created_at)}</div>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Activate dialog */}
      <Dialog open={dialog === 'activate'} onOpenChange={(o) => !o && closeDialog()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={FONT_HEAD}>Activate {inst.name}</DialogTitle>
            <DialogDescription>
              Creates the real Desk tenant with licence key &amp; institution code, plus the institution admin account. Your flat onboarding commission is queued automatically.
            </DialogDescription>
          </DialogHeader>
          {activationResult ? (
            <div className="space-y-3 text-sm" data-testid="activation-result">
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 space-y-2.5">
                {[
                  ['Institution code', activationResult.institution_code],
                  ['Licence key', activationResult.licence_key],
                  ['Admin login', activationResult.admin_login.email],
                  ['Login URL', activationResult.admin_login.login_url],
                ].map(([k, v]) => (
                  <div key={k} className="flex justify-between items-center">
                    <span className="text-slate-500 text-xs">{k}</span>
                    <span className="font-mono text-xs flex items-center gap-1.5">{v}
                      <button onClick={() => copy(v)} className="text-slate-300 hover:text-[#34A853]"><Copy className="w-3 h-3" /></button>
                    </span>
                  </div>
                ))}
              </div>
              <Button onClick={closeDialog} className="w-full text-white rounded-xl border-0" style={{ background: GRAD }}>Done</Button>
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Institution admin email *</Label>
                <Input data-testid="activate-admin-email" type="email" value={actForm.admin_email}
                  onChange={(e) => setActForm((f) => ({ ...f, admin_email: e.target.value }))} placeholder="principal@school.edu" className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">Admin password *</Label>
                <Input data-testid="activate-admin-password" type="password" value={actForm.admin_password}
                  onChange={(e) => setActForm((f) => ({ ...f, admin_password: e.target.value }))} placeholder="Set a temporary password" className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">Admin name</Label>
                <Input value={actForm.admin_name} onChange={(e) => setActForm((f) => ({ ...f, admin_name: e.target.value }))} placeholder={inst.contact_name || inst.name} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">Plan purchased *</Label>
                <Select value={actForm.plan_id} onValueChange={(v) => setActForm((f) => ({ ...f, plan_id: v }))}>
                  <SelectTrigger className="mt-1" data-testid="activate-plan-select"><SelectValue placeholder="Select a plan" /></SelectTrigger>
                  <SelectContent>
                    {plans.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name} — {fmtINR(p.price_per_license)}/licence x {p.license_limit.toLocaleString('en-IN')} = {fmtINR(p.price_per_license * p.license_limit)}/yr
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button data-testid="activate-submit" onClick={activate} disabled={busy} className="w-full text-white rounded-xl h-10 border-0" style={{ background: GRAD }}>
                {busy ? 'Activating…' : 'Activate & create tenant'}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Link tenant */}
      <Dialog open={dialog === 'link'} onOpenChange={(o) => !o && closeDialog()}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle style={FONT_HEAD}>Link existing tenant</DialogTitle>
            <DialogDescription>If this institution already signed up on Desk, link it using its institution code.</DialogDescription>
          </DialogHeader>
          <Input data-testid="link-code-input" value={linkCode} onChange={(e) => setLinkCode(e.target.value)} placeholder="e.g. AB12CD" className="font-mono" />
          <Button data-testid="link-submit" disabled={busy || !linkCode.trim()}
            onClick={() => act(async () => { await opsApi.post(`/institutions/${instId}/link-tenant`, { institution_code: linkCode }); closeDialog(); }, 'Linked to existing tenant')}
            className="w-full text-white rounded-xl border-0" style={{ background: GRAD }}>
            {busy ? 'Linking…' : 'Link tenant'}
          </Button>
        </DialogContent>
      </Dialog>

      {/* Discount request */}
      <Dialog open={dialog === 'discount'} onOpenChange={(o) => !o && closeDialog()}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle style={FONT_HEAD}>Request discount</DialogTitle>
            <DialogDescription>
              {bill?.tier ? `Tier ${bill.tier.name} suggests up to ${bill.tier.suggested_discount_pct}% by volume. ` : ''}
              Approval escalates to the role above you{user.role === 'super_admin' ? ' (auto-approved for Super Admin)' : ''}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Discount %</Label>
              <Input data-testid="discount-pct-input" type="number" min="1" max="100" value={discForm.discount_pct}
                onChange={(e) => setDiscForm((f) => ({ ...f, discount_pct: e.target.value }))} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">Reason / justification *</Label>
              <Textarea data-testid="discount-reason-input" value={discForm.reason} rows={2}
                onChange={(e) => setDiscForm((f) => ({ ...f, reason: e.target.value }))} className="mt-1" placeholder="e.g. Committed to 500+ students in Year 1" />
            </div>
            <Button data-testid="discount-submit-btn" disabled={busy || !discForm.discount_pct || !discForm.reason.trim()}
              onClick={() => act(async () => {
                await opsApi.post('/approvals', { kind: 'discount', institution_id: instId, discount_pct: Number(discForm.discount_pct), reason: discForm.reason });
                setDiscForm({ discount_pct: '', reason: '' });
                closeDialog();
              }, 'Discount request submitted')}
              className="w-full text-white rounded-xl border-0" style={{ background: GRAD }}>
              {busy ? 'Sending…' : 'Submit for approval'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Refund request */}
      <Dialog open={dialog === 'refund'} onOpenChange={(o) => !o && closeDialog()}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle style={FONT_HEAD}>Request refund</DialogTitle>
            <DialogDescription>Only paid invoices can be refunded. Escalates up the hierarchy for approval.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Paid invoice *</Label>
              <Select value={refundForm.invoice_id} onValueChange={(v) => setRefundForm((f) => ({ ...f, invoice_id: v }))}>
                <SelectTrigger className="mt-1" data-testid="refund-invoice-select"><SelectValue placeholder="Select invoice" /></SelectTrigger>
                <SelectContent>
                  {paidInvoices.map((i) => <SelectItem key={i.id} value={i.id}>{i.invoice_number} — {fmtINR2(i.total)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Refund amount (₹) *</Label>
              <Input data-testid="refund-amount-input" type="number" min="1" value={refundForm.amount}
                onChange={(e) => setRefundForm((f) => ({ ...f, amount: e.target.value }))} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">Reason *</Label>
              <Textarea data-testid="refund-reason-input" value={refundForm.reason} rows={2}
                onChange={(e) => setRefundForm((f) => ({ ...f, reason: e.target.value }))} className="mt-1" />
            </div>
            <Button data-testid="refund-submit-btn" disabled={busy || !refundForm.invoice_id || !refundForm.amount || !refundForm.reason.trim()}
              onClick={() => act(async () => {
                await opsApi.post('/approvals', { kind: 'refund', institution_id: instId, invoice_id: refundForm.invoice_id, amount: Number(refundForm.amount), reason: refundForm.reason });
                setRefundForm({ invoice_id: '', amount: '', reason: '' });
                closeDialog();
              }, 'Refund request submitted')}
              className="w-full text-white rounded-xl border-0" style={{ background: GRAD }}>
              {busy ? 'Sending…' : 'Submit for approval'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Ticket */}
      <Dialog open={dialog === 'ticket'} onOpenChange={(o) => !o && closeDialog()}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle style={FONT_HEAD}>Open resolution ticket</DialogTitle>
            <DialogDescription>Assigned to the institution's rep for resolution.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input data-testid="ticket-title-input" value={ticketForm.title} onChange={(e) => setTicketForm((f) => ({ ...f, title: e.target.value }))} placeholder="Issue title" />
            <Textarea value={ticketForm.description} rows={3} onChange={(e) => setTicketForm((f) => ({ ...f, description: e.target.value }))} placeholder="Describe the issue…" />
            <Select value={ticketForm.priority} onValueChange={(v) => setTicketForm((f) => ({ ...f, priority: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {['low', 'medium', 'high', 'urgent'].map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button data-testid="ticket-submit-btn" disabled={busy || !ticketForm.title.trim()}
              onClick={() => act(async () => {
                await opsApi.post(`/institutions/${instId}/tickets`, ticketForm);
                setTicketForm({ title: '', description: '', priority: 'medium' });
                closeDialog();
              }, 'Resolution ticket opened')}
              className="w-full text-white rounded-xl border-0" style={{ background: GRAD }}>
              {busy ? 'Opening…' : 'Open ticket'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
